import { formatMinorCompact } from '../lib/money';
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '../store/useAppStore';
import { qk } from '../services/queryKeys';
import {
  fetchEmployees, fetchLeaveRequests, fetchLeaveBalances, fetchHolidays,
  fetchAttendance, fetchLeaveTypes, fetchWorkflowInstances, fetchWorkflowTemplates,
  fetchPayrollRuns, fetchPayslips, fetchEmployeeDocuments,
  submitLeaveRequest as submitLeaveApi,
} from '../services/api';
import Avatar from '../components/Avatar';
import Chip from '../components/Chip';
import ProgressBar from '../components/ProgressBar';
import Icon from '../components/Icon';
import Modal from '../components/Modal';
import Button from '../components/Button';
import Alert from '../components/Alert';
import DatePicker from '../components/DatePicker';

function StatCard({ label, value, sub, icon, subColor }: { label: string; value: string | number; sub?: string; icon: string; subColor?: string }) {
  return (
    <div className="sk-stat">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <span className="stat-label">{label}</span>
        <Icon name={icon} size={16} color="var(--ink-3)" />
      </div>
      <div className="stat-value">{value}</div>
      {sub && <div className="stat-sub" style={subColor ? { color: subColor } : {}}>{sub}</div>}
    </div>
  );
}

function fmtLakh(n: number) {
  if (n >= 100000) return `₹${(n / 100000).toFixed(2)}L`;
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);
}

export default function Home() {
  const { role, profileId, employeeId, firstName: storedFirstName, modal, openModal, closeModal, toast } = useAppStore();
  const myId = employeeId || profileId;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [showAllHolidays, setShowAllHolidays] = useState(false);
  const [leaveForm, setLeaveForm] = useState({ leaveTypeId: '', startDate: '', endDate: '', reason: '' });

  const today = new Date().toISOString().slice(0, 10);
  const thisMonth = today.slice(0, 7);

  const { data: employees = [] } = useQuery({ queryKey: qk.employees(), queryFn: fetchEmployees });
  const { data: leaveRequests = [] } = useQuery({ queryKey: qk.leaveRequests(), queryFn: () => fetchLeaveRequests() });
  const { data: leaveBalances = [] } = useQuery({ queryKey: qk.leaveBalances(myId), queryFn: () => fetchLeaveBalances(myId), enabled: !!employeeId });
  const { data: leaveTypes = [] } = useQuery({ queryKey: qk.leaveTypes(), queryFn: fetchLeaveTypes });
  const { data: holidays = [] } = useQuery({ queryKey: qk.holidays(), queryFn: fetchHolidays });
  const { data: attendanceData = [] } = useQuery({ queryKey: qk.attendance(myId), queryFn: () => fetchAttendance(myId), enabled: !!employeeId });
  const { data: workflowInstances = [] } = useQuery({ queryKey: qk.workflowInstances(), queryFn: () => fetchWorkflowInstances() });
  const { data: workflowTemplates = [] } = useQuery({ queryKey: qk.workflowTemplates(), queryFn: fetchWorkflowTemplates, enabled: role === 'hr' });
  const { data: documents = [] } = useQuery({ queryKey: qk.documents(myId), queryFn: () => fetchEmployeeDocuments(myId), enabled: !!employeeId });
  const { data: payrollRuns = [] } = useQuery({ queryKey: qk.payrollRuns(), queryFn: fetchPayrollRuns, enabled: role === 'ess' });
  const latestRunId = payrollRuns[0]?.id;
  const { data: payslips = [] } = useQuery({ queryKey: qk.payslips(latestRunId ?? ''), queryFn: () => fetchPayslips(latestRunId!), enabled: !!latestRunId && role === 'ess' });

  const me = employees.find((e) => e.id === myId) ?? employees.find((e) => e.platformUserId === profileId);
  const displayFirstName = me?.firstName || storedFirstName || 'there';

  const activeEmps = employees.filter((e) => e.status === 'active').length;
  const onLeave = employees.filter((e) => e.status === 'on_leave').length;
  const pendingLeaves = leaveRequests.filter((r) => r.status === 'submitted');

  // HR: attention needed items
  const attentionItems = React.useMemo(() => {
    if (role !== 'hr') return [];
    const items: Array<{ id: string; firstName: string; lastName?: string; avatarColor?: string; title: string; sub: string; chipLabel: string; chipVariant: 'danger' | 'info' | 'warn' | 'neutral' }> = [];
    const inProgress = workflowInstances.filter(i => i.state === 'in_progress');
    for (const inst of inProgress.slice(0, 5)) {
      const emp = employees.find(e => e.id === inst.employeeId);
      if (!emp) continue;
      const tmpl = workflowTemplates.find(t => t.id === inst.templateId);
      const trigger = (tmpl as any)?.trigger ?? 'joining';
      let chipLabel: string, chipVariant: 'danger' | 'info' | 'warn' | 'neutral', sub: string;
      if (trigger === 'separation' || trigger === 'exit') {
        chipLabel = 'separation'; chipVariant = 'danger';
        sub = `Exit workflow · ${inst.blockingRemaining ?? 0} step${inst.blockingRemaining !== 1 ? 's' : ''} remaining`;
      } else if (trigger === 'confirmation' || trigger === 'probation') {
        chipLabel = 'confirm'; chipVariant = 'warn';
        sub = 'Confirmation review pending';
      } else {
        chipLabel = 'joining'; chipVariant = 'info';
        const pending = (inst as any).stepInstances?.filter((s: any) => s.status !== 'approved').length ?? inst.blockingRemaining ?? 0;
        sub = `Onboarding · ${pending} step${pending !== 1 ? 's' : ''} pending`;
      }
      items.push({ id: inst.id, firstName: emp.firstName, lastName: emp.lastName, avatarColor: emp.avatarColor, title: `${emp.firstName} ${emp.lastName ?? ''}`, sub, chipLabel, chipVariant });
    }
    // Probation ending within 30 days (not already in workflow)
    const thirtyDays = new Date(today);
    thirtyDays.setDate(thirtyDays.getDate() + 30);
    const thirtyStr = thirtyDays.toISOString().slice(0, 10);
    for (const emp of employees) {
      const conf = (emp as any).confirmationDate as string | undefined;
      if (!conf) continue;
      const confD = conf.slice(0, 10);
      if (confD >= today && confD <= thirtyStr && !items.find(i => i.id.startsWith('wf') && i.firstName === emp.firstName)) {
        items.push({ id: `prob_${emp.id}`, firstName: emp.firstName, lastName: emp.lastName, avatarColor: emp.avatarColor, title: `${emp.firstName} ${emp.lastName ?? ''}`, sub: `Probation ends ${new Date(confD).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} — review due`, chipLabel: 'confirm', chipVariant: 'warn' });
      }
    }
    return items.slice(0, 6);
  }, [role, workflowInstances, workflowTemplates, employees, today]);

  // HR: headcount by department
  const deptCounts = React.useMemo(() => {
    const counts: Record<string, number> = {};
    employees.filter(e => e.status === 'active').forEach(e => {
      const dept = (e as any).department?.name || e.departmentId || 'Other';
      counts[dept] = (counts[dept] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [employees]);
  const maxDeptCount = deptCounts.reduce((m, [, n]) => Math.max(m, n), 1);

  // ESS stat computations
  const myBalances = leaveTypes.map((lt) => ({
    ...lt,
    leaveTypeId: lt.id,
    balance: Number(leaveBalances.find((b) => b.leaveTypeId === lt.id)?.balance ?? 0),
  }));
  const primaryLeave = myBalances.find(b => b.code === 'EL') || myBalances.find(b => b.code === 'AL') || myBalances[0];

  // This month attendance
  const monthPunches = attendanceData.filter(a => a.punchedAt.startsWith(thisMonth));
  const dayMap = new Map<string, { firstTs: number; lastTs: number }>();
  for (const punch of monthPunches) {
    const date = punch.punchedAt.slice(0, 10);
    const ts = new Date(punch.punchedAt).getTime();
    const prev = dayMap.get(date);
    dayMap.set(date, { firstTs: prev ? Math.min(prev.firstTs, ts) : ts, lastTs: prev ? Math.max(prev.lastTs, ts) : ts });
  }
  const daysPresent = dayMap.size;
  const daysWithRange = Array.from(dayMap.values()).filter(d => d.lastTs > d.firstTs + 60_000);
  const avgHoursMs = daysWithRange.length > 0 ? daysWithRange.reduce((s, d) => s + (d.lastTs - d.firstTs), 0) / daysWithRange.length : 0;
  const avgH = Math.floor(avgHoursMs / 3600000);
  const avgM = Math.floor((avgHoursMs % 3600000) / 60000);
  const thisMonthSub = avgH > 0 ? `avg ${avgH}h ${String(avgM).padStart(2, '0')}m` : 'days present';

  // Pending actions
  const pendingDocs = documents.filter(d => d.requiresAck && !d.ackedAt).length;

  // Last payslip
  const myPayslip = payslips.find(p => p.employeeId === myId) ?? payslips.find(p => p.employeeId === employeeId);
  const lastPayslipNet = myPayslip ? formatMinorCompact(myPayslip.netPayMinor, myPayslip.currencyCode) : '—';
  const lastPayslipSub = myPayslip ? `${myPayslip.periodLabel} · net` : 'no payslip yet';

  const todayHoliday = holidays.find((h) => h.date === today);
  const todayPunches = attendanceData.filter(e => e.punchedAt.startsWith(today));
  const isClockedIn = todayPunches.length > 0 && [...todayPunches].sort((a, b) => b.punchedAt.localeCompare(a.punchedAt))[0]?.punchType === 'clock_in';

  // Apply leave
  const calcDays = () => {
    if (!leaveForm.startDate || !leaveForm.endDate) return 0;
    let count = 0;
    const cur = new Date(leaveForm.startDate + 'T00:00:00');
    const end = new Date(leaveForm.endDate + 'T00:00:00');
    while (cur <= end) { const d = cur.getDay(); if (d !== 0 && d !== 6) count++; cur.setDate(cur.getDate() + 1); }
    return count;
  };
  const effectiveLeaveTypeId = leaveForm.leaveTypeId || leaveTypes[0]?.id || '';
  const submitMutation = useMutation({
    mutationFn: submitLeaveApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.leaveRequests() });
      toast('Leave request submitted');
      closeModal();
      setLeaveForm({ leaveTypeId: '', startDate: '', endDate: '', reason: '' });
    },
    onError: () => toast('Failed to submit leave request', 'danger'),
  });

  // My requests (ESS own leave history)
  const myLeaveRequests = leaveRequests.filter(r => r.employeeId === myId).slice(0, 5);

  return (
    <div>
      <div className="sk-page-header">
        <div>
          <h1>Good morning, {displayFirstName}</h1>
          <p style={{ color: 'var(--ink-2)', fontSize: 'var(--fs-sm)', marginTop: 4 }}>
            {todayHoliday ? `🎉 Today is ${todayHoliday.name}` : `${new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}`}
          </p>
        </div>
        {role === 'ess' ? (
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="accent" onClick={() => openModal('apply_leave_home')}>
              <Icon name="plus" size={14} color="white" />
              Apply leave
            </Button>
          </div>
        ) : role === 'hr' ? (
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="outline" onClick={() => openModal('add_employee')}>
              <Icon name="plus" size={14} color="currentColor" />
              Add employee
            </Button>
            <Button variant="accent" onClick={() => navigate('/workflows')}>
              <Icon name="flow" size={14} color="white" />
              Workflows
            </Button>
          </div>
        ) : (
          <Chip variant={isClockedIn ? 'success' : 'neutral'} dot>
            {isClockedIn ? 'Clocked in' : 'Not clocked in'}
          </Chip>
        )}
      </div>

      {/* Stats */}
      <div className="stats-grid" style={{ marginBottom: 24 }}>
        {role === 'ess' && (
          <>
            <StatCard
              label="Annual leave left"
              value={primaryLeave?.balance ?? 0}
              sub={`of ${primaryLeave?.annualDays ?? 0} days`}
              icon="calendar"
            />
            <StatCard
              label="This month"
              value={`${daysPresent}d`}
              sub={thisMonthSub}
              icon="clock"
              subColor={avgH > 0 ? 'var(--success)' : undefined}
            />
            <StatCard
              label="Pending actions"
              value={pendingDocs}
              sub={pendingDocs === 1 ? 'doc to acknowledge' : 'docs to acknowledge'}
              icon="doc"
              subColor={pendingDocs > 0 ? 'var(--warn)' : undefined}
            />
            <StatCard
              label="Last payslip"
              value={lastPayslipNet}
              sub={lastPayslipSub}
              icon="wallet"
            />
          </>
        )}
        {role === 'mss' && (
          <>
            <StatCard label="Team size" value={employees.filter(e => e.managerId === myId && e.status === 'active').length} sub="direct reports" icon="users" />
            <StatCard label="On leave today" value={leaveRequests.filter(r => r.status === 'approved' && r.startDate <= today && r.endDate >= today && employees.find(e => e.id === r.employeeId)?.managerId === profileId).length} sub="team members" icon="calendar" />
            <StatCard label="Pending approvals" value={pendingLeaves.filter(r => employees.find(e => e.id === r.employeeId)?.managerId === profileId).length} sub="leave requests" icon="warn" />
            <StatCard label="Attendance" value={todayPunches.length > 0 ? 'Present' : 'Absent'} sub="Today" icon="clock" />
          </>
        )}
        {role === 'hr' && (
          <>
            <StatCard label="Headcount" value={activeEmps} sub="active employees" icon="users" />
            <StatCard label="Running workflows" value={workflowInstances.filter(i => i.state === 'in_progress').length} sub="onboarding / exits" icon="flow" subColor="var(--accent)" />
            <StatCard label="Leave requests" value={pendingLeaves.length} sub="org-wide pending" icon="calendar" subColor={pendingLeaves.length > 0 ? 'var(--warn)' : undefined} />
          </>
        )}
      </div>

      {role === 'hr' ? (
        /* ── HR two-col: Attention needed + Headcount by dept ── */
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 20, alignItems: 'start' }}>
          <div className="t-card">
            <div className="card-head" style={{ paddingBottom: 12 }}>
              <div>
                <h3>Attention needed</h3>
                <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)', marginTop: 2 }}>Surfaced by Knack AI</div>
              </div>
            </div>
            {attentionItems.length === 0 ? (
              <div className="card-body" style={{ color: 'var(--ink-3)', fontSize: 'var(--fs-sm)' }}>Nothing needs attention right now.</div>
            ) : (
              <div>
                {attentionItems.map(item => (
                  <div key={item.id} className="sk-listrow">
                    <Avatar firstName={item.firstName} lastName={item.lastName} color={item.avatarColor} size="sm" />
                    <div className="row-main">
                      <div className="row-title">{item.title}</div>
                      <div className="row-sub">{item.sub}</div>
                    </div>
                    <Chip variant={item.chipVariant}>{item.chipLabel}</Chip>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="t-card">
            <div className="card-head" style={{ paddingBottom: 12 }}>
              <h3>Headcount by dept</h3>
              <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)' }}>{activeEmps} active</span>
            </div>
            <div className="card-body" style={{ paddingTop: 8 }}>
              {deptCounts.length === 0 ? (
                <div style={{ color: 'var(--ink-3)', fontSize: 'var(--fs-sm)' }}>No department data.</div>
              ) : deptCounts.map(([dept, count]) => (
                <div key={dept} style={{ marginBottom: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-2)' }}>{dept}</span>
                    <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 600, color: 'var(--ink)' }}>{count}</span>
                  </div>
                  <ProgressBar value={count} max={maxDeptCount} variant="accent" />
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        /* ── ESS / MSS two-col ── */
        <div className="two-col">
          {/* Main card */}
          <div>
            <div className="t-card">
              <div className="card-head" style={{ paddingBottom: 12 }}>
                <h3>{role === 'ess' ? 'My requests' : 'Activity'}</h3>
                {role === 'ess' && <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)' }}>Recent activity</span>}
              </div>
              {role === 'ess' ? (
                myLeaveRequests.length === 0 ? (
                  <div className="card-body" style={{ color: 'var(--ink-3)', fontSize: 'var(--fs-sm)' }}>No leave requests yet.</div>
                ) : (
                  <div>
                    {myLeaveRequests.map((r) => {
                      const lt = leaveTypes.find((l) => l.id === r.leaveTypeId);
                      const fmtD = (d: string) => new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
                      return (
                        <div key={r.id} className="sk-listrow">
                          <Avatar firstName={me?.firstName ?? ''} lastName={me?.lastName} color={me?.avatarColor} size="sm" />
                          <div className="row-main">
                            <div className="row-title">{lt?.name ?? 'Leave'} · {Number(r.days)}d</div>
                            <div className="row-sub">{fmtD(r.startDate)} → {fmtD(r.endDate)}</div>
                          </div>
                          <Chip variant={r.status === 'approved' ? 'success' : r.status === 'submitted' ? 'warn' : r.status === 'rejected' ? 'danger' : 'neutral'}>
                            {r.status}
                          </Chip>
                        </div>
                      );
                    })}
                  </div>
                )
              ) : (
                (() => {
                  const feedItems = [
                    ...leaveRequests.filter((r) => r.status === 'submitted').slice(0, 3).map((r) => {
                      const emp = employees.find((e) => e.id === r.employeeId);
                      const lt = leaveTypes.find((l) => l.id === r.leaveTypeId);
                      const empDisplay = emp ?? r.employee;
                      return {
                        id: r.id, icon: 'calendar',
                        title: `${empDisplay ? `${empDisplay.firstName} ${empDisplay.lastName}` : r.employeeId} applied for ${lt?.name ?? 'leave'}`,
                        sub: `${r.startDate} → ${r.endDate} · ${r.days} day${Number(r.days) > 1 ? 's' : ''}`,
                        badge: 'pending' as const, time: r.createdAt.slice(0, 10),
                      };
                    }),
                    ...employees.filter((e) => e.startDate === today).map((e) => ({
                      id: e.id, icon: 'users', title: `${e.firstName} ${e.lastName} joined today`,
                      sub: `${e.jobTitle}`, badge: 'info' as const, time: 'Today',
                    })),
                  ];
                  return feedItems.length === 0 ? (
                    <div className="card-body" style={{ color: 'var(--ink-3)', fontSize: 'var(--fs-sm)' }}>All quiet — nothing pending.</div>
                  ) : (
                    <div>
                      {feedItems.map((item) => (
                        <div key={item.id} className="sk-listrow">
                          <div style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--neutral-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <Icon name={item.icon} size={14} color="var(--ink-2)" />
                          </div>
                          <div className="row-main">
                            <div className="row-title">{item.title}</div>
                            <div className="row-sub">{item.sub}</div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                            <Chip variant={item.badge === 'pending' ? 'warn' : 'info'}>{item.badge}</Chip>
                            <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)' }}>{item.time}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()
              )}
            </div>
          </div>

          {/* Side panel */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Leave balances */}
            {role === 'ess' && (
              <div className="t-card">
                <div className="card-head" style={{ paddingBottom: 12 }}>
                  <h3>Leave balances</h3>
                </div>
                <div className="card-body" style={{ paddingTop: 8 }}>
                  {myBalances.map((b) => (
                    <div key={b.leaveTypeId} style={{ marginBottom: 14 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                        <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-2)' }}>{b.name}</span>
                        <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 600, color: 'var(--ink)' }}>{b.balance} / {b.annualDays}</span>
                      </div>
                      <ProgressBar value={b.balance} max={b.annualDays} variant={b.balance < 3 ? 'warn' : 'accent'} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Team presence for MSS */}
            {role === 'mss' && (
              <div className="t-card">
                <div className="card-head" style={{ paddingBottom: 12 }}>
                  <h3>Team today</h3>
                </div>
                <div className="card-body" style={{ paddingTop: 8 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div style={{ textAlign: 'center', padding: '12px 0' }}>
                      <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--success)' }}>{employees.filter(e => e.managerId === myId && e.status === 'active').length}</div>
                      <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)', marginTop: 2 }}>Active</div>
                    </div>
                    <div style={{ textAlign: 'center', padding: '12px 0' }}>
                      <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--warn)' }}>{leaveRequests.filter(r => r.status === 'approved' && r.startDate <= today && r.endDate >= today && employees.find(e => e.id === r.employeeId)?.managerId === myId).length}</div>
                      <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)', marginTop: 2 }}>On Leave</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Upcoming holidays */}
            <div className="t-card">
              <div className="card-head" style={{ paddingBottom: 12 }}>
                <h3>Upcoming holidays</h3>
                <button
                  onClick={() => setShowAllHolidays(true)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 'var(--fs-xs)', color: 'var(--accent)', fontWeight: 500, padding: 0 }}
                >
                  View all
                </button>
              </div>
              <div>
                {holidays.filter(h => h.date >= today).slice(0, 3).map((h) => (
                  <div key={h.id} className="sk-listrow" style={{ padding: '10px 16px' }}>
                    <div className="row-main">
                      <div className="row-title">{h.name}</div>
                      <div className="row-sub">{new Date(h.date).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}</div>
                    </div>
                    <Chip variant={h.type === 'national' ? 'info' : 'neutral'}>{h.type}</Chip>
                  </div>
                ))}
              </div>
            </div>

            {showAllHolidays && (
              <Modal title="Holiday calendar" onClose={() => setShowAllHolidays(false)}>
                <table className="t-table">
                  <thead><tr><th>Holiday</th><th>Date</th><th>Day</th><th>Type</th></tr></thead>
                  <tbody>
                    {[...holidays].sort((a, b) => a.date.localeCompare(b.date)).map((h) => {
                      const isPast = h.date < today;
                      return (
                        <tr key={h.id} style={{ opacity: isPast ? 0.45 : 1 }}>
                          <td>{h.name}</td>
                          <td>{new Date(h.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                          <td style={{ color: 'var(--ink-3)' }}>{new Date(h.date).toLocaleDateString('en-IN', { weekday: 'short' })}</td>
                          <td><Chip variant={h.type === 'national' ? 'info' : 'neutral'}>{h.type}</Chip></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Modal>
            )}
          </div>
        </div>
      )}

      {/* Apply Leave Modal (from Home) */}
      {modal.type === 'apply_leave_home' && (
        <Modal
          title="Apply for leave"
          onClose={closeModal}
          footer={
            <>
              <Button variant="ghost" onClick={closeModal}>Cancel</Button>
              <Button
                variant="accent"
                disabled={submitMutation.isPending}
                onClick={() => {
                  if (!leaveForm.startDate || !leaveForm.endDate) { toast('Please select dates', 'warn'); return; }
                  if (leaveForm.endDate < leaveForm.startDate) { toast('"To" date cannot be before "From" date', 'warn'); return; }
                  submitMutation.mutate({
                    employeeId: myId,
                    leaveTypeId: effectiveLeaveTypeId,
                    startDate: leaveForm.startDate,
                    endDate: leaveForm.endDate,
                    days: calcDays(),
                    reason: leaveForm.reason,
                  });
                }}
              >Submit request</Button>
            </>
          }
        >
          <div className="form-row">
            <label>Leave type</label>
            <select className="t-input" value={effectiveLeaveTypeId} onChange={(e) => setLeaveForm((f) => ({ ...f, leaveTypeId: e.target.value }))}>
              {leaveTypes.map((lt) => {
                const bal = leaveBalances.find((b) => b.leaveTypeId === lt.id)?.balance ?? 0;
                return <option key={lt.id} value={lt.id}>{lt.name} ({bal} days remaining)</option>;
              })}
            </select>
          </div>
          <div className="form-grid">
            <div className="form-row">
              <label>From</label>
              <DatePicker value={leaveForm.startDate} min={today} disableWeekends onChange={(v) => setLeaveForm((f) => ({ ...f, startDate: v, endDate: f.endDate && f.endDate < v ? '' : f.endDate }))} placeholder="From date" />
            </div>
            <div className="form-row">
              <label>To</label>
              <DatePicker value={leaveForm.endDate} min={leaveForm.startDate || today} disableWeekends onChange={(v) => setLeaveForm((f) => ({ ...f, endDate: v }))} placeholder="To date" />
            </div>
          </div>
          {calcDays() > 0 && (
            <Alert variant="info" style={{ marginBottom: 14 }}>{calcDays()} working day{calcDays() > 1 ? 's' : ''} — weekends &amp; holidays excluded</Alert>
          )}
          <div className="form-row">
            <label>Reason <span style={{ color: 'var(--ink-3)', fontWeight: 400 }}>(optional)</span></label>
            <textarea className="t-input" rows={3} style={{ height: 'auto', padding: '8px 10px', resize: 'vertical' }} value={leaveForm.reason} onChange={(e) => setLeaveForm((f) => ({ ...f, reason: e.target.value }))} placeholder="Add a note for your manager…" />
          </div>
        </Modal>
      )}
    </div>
  );
}
